// Themed form primitives for the console — the design-system form layer over
// Radix Select/Popover, react-day-picker and cmdk so we stop using unstyled
// native <select>/<input type=date>. All portal content sits at z-[200] so it
// renders above drawers, the command palette and the Trading zen overlay.

import { forwardRef, useState } from "react";
import type { ReactNode, CSSProperties, InputHTMLAttributes, TextareaHTMLAttributes } from "react";
import type { LucideIcon } from "lucide-react";
import { Calendar as CalendarIcon, Check, ChevronDown, Search } from "lucide-react";
import * as RSelect from "@radix-ui/react-select";
import * as RPopover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { DayPicker, type DateRange } from "react-day-picker";
import { format } from "date-fns";
import "react-day-picker/dist/style.css";

export type { DateRange };

// `min-w-0` (so the trigger can shrink inside a flex/grid track instead of
// forcing it wider) + `max-w-full` + a shrinkable, truncating label is what
// keeps a trigger from ever causing horizontal page overflow. The default is
// intrinsic width (no min-w) so toolbar dropdowns size to content; callers that
// want a full-width field pass `className="w-full"`.
const TRIGGER =
  "inline-flex min-w-0 max-w-full items-center justify-between gap-2 rounded-control border border-line-strong bg-surface-2 px-3 text-sm text-text-1 transition-colors duration-fast hover:border-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 disabled:cursor-not-allowed disabled:opacity-50";
// 32 / 36 px with a fine pointer; 44 px and 16 px text under a coarse one (so
// iOS Safari never zooms on focus) — at any viewport width.
const SIZE = { sm: "h-8 coarse:h-11 coarse:text-base", md: "h-9 coarse:h-11 coarse:text-base" } as const;
// Shared field styling for native <input>/<textarea> so they match the triggers.
const FIELD =
  "w-full rounded-control border border-line-strong bg-surface-2 px-3 text-sm text-text-1 placeholder-text-3 transition-colors duration-fast hover:border-white/25 focus:outline-none focus-visible:border-brand/60 focus-visible:ring-2 focus-visible:ring-brand/25 disabled:cursor-not-allowed disabled:opacity-50 coarse:text-base";
const CONTENT =
  "z-popover max-w-[calc(100vw-1rem)] overflow-hidden rounded-card border border-line-strong bg-surface-3 p-1 shadow-[0_16px_48px_rgba(0,0,0,0.5)]";

// ---------------------------------------------------------------------------
// Select (Radix) — single value, keyboard + portal
// ---------------------------------------------------------------------------

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
  disabled?: boolean;
}

// Radix Select forbids an empty-string Item value (it reserves "" to clear the
// selection). Map "" <-> a sentinel so callers can still use an empty-value
// "All" option transparently.
const EMPTY_VALUE = "__pb_empty__";
const toRadix = (v: string) => (v === "" ? EMPTY_VALUE : v);
const fromRadix = (v: string) => (v === EMPTY_VALUE ? "" : v);

export function Select<T extends string>({
  value,
  onChange,
  options,
  placeholder = "Select…",
  ariaLabel,
  size = "md",
  disabled,
  className,
}: {
  value: T | undefined;
  onChange: (value: T) => void;
  options: ReadonlyArray<SelectOption<T>>;
  placeholder?: string;
  ariaLabel: string;
  size?: "sm" | "md";
  disabled?: boolean;
  className?: string;
}) {
  return (
    <RSelect.Root
      value={value === undefined ? undefined : toRadix(value)}
      onValueChange={(v) => onChange(fromRadix(v) as T)}
      disabled={disabled}
    >
      <RSelect.Trigger aria-label={ariaLabel} className={`${TRIGGER} ${SIZE[size]} ${className ?? ""}`}>
        <span className="min-w-0 flex-1 truncate text-left">
          <RSelect.Value placeholder={<span className="text-text-3">{placeholder}</span>} />
        </span>
        <RSelect.Icon>
          <ChevronDown className="h-4 w-4 shrink-0 text-text-3" />
        </RSelect.Icon>
      </RSelect.Trigger>
      <RSelect.Portal>
        <RSelect.Content position="popper" sideOffset={6} className={CONTENT}>
          <RSelect.Viewport className="max-h-[--radix-select-content-available-height] min-w-[--radix-select-trigger-width]">
            {options.map((o) => {
              const Icon = o.icon;
              return (
                <RSelect.Item
                  key={o.value || EMPTY_VALUE}
                  value={toRadix(o.value)}
                  disabled={o.disabled}
                  className="flex cursor-pointer items-center justify-between gap-3 rounded-[6px] px-2 py-1.5 text-sm text-text-2 outline-none data-[disabled]:cursor-not-allowed data-[highlighted]:bg-white/[0.06] data-[highlighted]:text-text-1 data-[disabled]:opacity-40 coarse:min-h-11"
                >
                  <span className="flex items-center gap-2">
                    {Icon && <Icon className="h-3.5 w-3.5 text-text-3" />}
                    <RSelect.ItemText>{o.label}</RSelect.ItemText>
                  </span>
                  <RSelect.ItemIndicator>
                    <Check className="h-3.5 w-3.5 text-ok" />
                  </RSelect.ItemIndicator>
                </RSelect.Item>
              );
            })}
          </RSelect.Viewport>
        </RSelect.Content>
      </RSelect.Portal>
    </RSelect.Root>
  );
}

// ---------------------------------------------------------------------------
// Combobox — searchable single-select (cmdk in a popover)
// ---------------------------------------------------------------------------

export function Combobox<T extends string>({
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  ariaLabel,
  size = "md",
  className,
}: {
  value: T | undefined;
  onChange: (value: T) => void;
  options: ReadonlyArray<SelectOption<T>>;
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel: string;
  size?: "sm" | "md";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <RPopover.Root open={open} onOpenChange={setOpen}>
      <RPopover.Trigger asChild>
        <button type="button" aria-label={ariaLabel} className={`${TRIGGER} ${SIZE[size]} ${className ?? ""}`}>
          <span className={`min-w-0 flex-1 truncate text-left ${selected ? "text-text-1" : "text-text-3"}`}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-text-3" />
        </button>
      </RPopover.Trigger>
      <RPopover.Portal>
        <RPopover.Content sideOffset={6} align="start" className={`${CONTENT} w-[--radix-popover-trigger-width] min-w-[220px] p-0`}>
          <Command className="overflow-hidden" loop>
            <div className="flex items-center gap-2 border-b border-line px-2.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-text-3" />
              <Command.Input placeholder={searchPlaceholder} className="h-9 w-full bg-transparent text-sm text-text-1 placeholder-text-3 outline-none coarse:h-11 coarse:text-base" />
            </div>
            <Command.List className="max-h-60 overflow-y-auto p-1">
              <Command.Empty className="py-4 text-center text-sm text-text-3">No results.</Command.Empty>
              {options.map((o) => (
                <Command.Item
                  key={o.value}
                  value={`${o.label} ${o.value}`}
                  disabled={o.disabled}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center justify-between gap-3 rounded-[6px] px-2 py-1.5 text-sm text-text-2 outline-none data-[selected=true]:bg-white/[0.06] data-[selected=true]:text-text-1 coarse:min-h-11"
                >
                  {o.label}
                  {value === o.value && <Check className="h-3.5 w-3.5 text-ok" />}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}

// ---------------------------------------------------------------------------
// Calendar (react-day-picker, dark-themed via .rdp-pb in index.css)
// ---------------------------------------------------------------------------

const RDP_VARS: CSSProperties = {
  // @ts-expect-error CSS custom properties
  "--rdp-cell-size": "max(38px, var(--pb-touch))", // 38 desktop · 44 touch
  margin: 0,
};

export function Calendar(props: React.ComponentProps<typeof DayPicker>) {
  return <DayPicker showOutsideDays className="rdp-pb" style={RDP_VARS} {...props} />;
}

// ---------------------------------------------------------------------------
// DatePicker (single) + DateRangePicker
// ---------------------------------------------------------------------------

function PopoverField({
  label,
  ariaLabel,
  size = "md",
  className,
  children,
}: {
  label: ReactNode;
  ariaLabel: string;
  size?: "sm" | "md";
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <RPopover.Root open={open} onOpenChange={setOpen}>
      <RPopover.Trigger asChild>
        <button type="button" aria-label={ariaLabel} className={`${TRIGGER} ${SIZE[size]} ${className ?? ""}`}>
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-text-3" />
            <span className="min-w-0 truncate">{label}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-text-3" />
        </button>
      </RPopover.Trigger>
      <RPopover.Portal>
        <RPopover.Content sideOffset={6} align="start" className={`${CONTENT} p-2`} onOpenAutoFocus={(e) => e.preventDefault()}>
          {/* render-prop so the calendar can close the popover on select */}
          {typeof children === "function" ? (children as (close: () => void) => ReactNode)(() => setOpen(false)) : children}
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  ariaLabel = "Pick a date",
  size = "md",
  className,
}: {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  ariaLabel?: string;
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <PopoverField
      ariaLabel={ariaLabel}
      size={size}
      className={className}
      label={value ? <span className="text-text-1">{format(value, "d MMM yyyy")}</span> : <span className="text-text-3">{placeholder}</span>}
    >
      {((close: () => void) => (
        <Calendar
          mode="single"
          selected={value}
          onSelect={(d) => {
            onChange(d ?? undefined);
            if (d) close();
          }}
        />
      )) as unknown as ReactNode}
    </PopoverField>
  );
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = "Pick a range",
  ariaLabel = "Pick a date range",
  size = "md",
  className,
}: {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  placeholder?: string;
  ariaLabel?: string;
  size?: "sm" | "md";
  className?: string;
}) {
  const label =
    value?.from && value?.to
      ? `${format(value.from, "d MMM")} – ${format(value.to, "d MMM yyyy")}`
      : value?.from
        ? `${format(value.from, "d MMM yyyy")} – …`
        : null;
  return (
    <PopoverField
      ariaLabel={ariaLabel}
      size={size}
      className={className}
      label={label ? <span className="text-text-1">{label}</span> : <span className="text-text-3">{placeholder}</span>}
    >
      <Calendar
        mode="range"
        numberOfMonths={2}
        selected={value}
        onSelect={(r) => onChange(r ?? undefined)}
      />
    </PopoverField>
  );
}

// ---------------------------------------------------------------------------
// Field — label + optional hint/error wrapper for any control. `min-w-0` so a
// field placed in a flex/grid track can shrink instead of overflowing.
// ---------------------------------------------------------------------------

export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  className,
  children,
}: {
  label?: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`min-w-0 ${className ?? ""}`}>
      {label && (
        <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-text-2">
          {label}
          {required && <span className="ml-0.5 text-text-3">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="mt-1 text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-text-3">{hint}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Input / Textarea — themed native controls (match the trigger styling)
// ---------------------------------------------------------------------------

export const Input = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & { size?: "sm" | "md"; icon?: LucideIcon; invalid?: boolean }
>(function Input({ size = "md", icon: Icon, invalid, className, ...rest }, ref) {
  const invalidCls = invalid ? "border-danger/60 focus-visible:border-danger focus-visible:ring-danger/25" : "";
  if (Icon) {
    return (
      <div className="relative w-full min-w-0">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-3" aria-hidden="true" />
        <input ref={ref} className={`${FIELD} ${SIZE[size]} pl-9 ${invalidCls} ${className ?? ""}`} {...rest} />
      </div>
    );
  }
  return <input ref={ref} className={`${FIELD} ${SIZE[size]} ${invalidCls} ${className ?? ""}`} {...rest} />;
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ className, invalid, rows = 3, ...rest }, ref) {
  const invalidCls = invalid ? "border-danger/60 focus-visible:border-danger focus-visible:ring-danger/25" : "";
  return <textarea ref={ref} rows={rows} className={`${FIELD} py-2 leading-relaxed ${invalidCls} ${className ?? ""}`} {...rest} />;
});

// ---------------------------------------------------------------------------
// Checkbox — single accessible toggle (whole row clickable, no double-fire)
// ---------------------------------------------------------------------------

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  className,
  labelClassName,
  descriptionClassName,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Override the default label styling (e.g. to match a warning context). */
  labelClassName?: string;
  descriptionClassName?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`flex w-full items-start gap-2.5 text-left disabled:cursor-not-allowed disabled:opacity-50 coarse:min-h-11 coarse:py-1 ${className ?? ""}`}
    >
      <span
        className={[
          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors duration-fast",
          checked ? "border-brand bg-brand text-black" : "border-line-strong bg-surface-2 text-transparent",
        ].join(" ")}
      >
        <Check className="h-3 w-3" aria-hidden="true" />
      </span>
      {(label || description) && (
        <span className="min-w-0">
          {label && <span className={labelClassName ?? "block text-sm text-text-1"}>{label}</span>}
          {description && <span className={descriptionClassName ?? "mt-0.5 block text-xs text-text-3"}>{description}</span>}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// RadioGroup — single-select option list
// ---------------------------------------------------------------------------

export interface RadioOption<T extends string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export function RadioGroup<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className,
  disabled,
}: {
  value: T | undefined;
  onChange: (value: T) => void;
  options: ReadonlyArray<RadioOption<T>>;
  ariaLabel: string;
  className?: string;
  /** Disable the whole group. */
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={`space-y-2 ${className ?? ""}`}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={o.disabled || disabled}
            onClick={() => onChange(o.value)}
            className="flex w-full items-start gap-2.5 text-left disabled:cursor-not-allowed disabled:opacity-50 coarse:min-h-11 coarse:py-1"
          >
            <span
              className={[
                "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-fast",
                active ? "border-brand" : "border-line-strong bg-surface-2",
              ].join(" ")}
            >
              {active && <span className="h-2 w-2 rounded-full bg-brand" />}
            </span>
            <span className="min-w-0">
              <span className="block text-sm text-text-1">{o.label}</span>
              {o.description && <span className="mt-0.5 block text-xs text-text-3">{o.description}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
